// components/TaxPropertyCard.tsx
import React, { useState } from 'react';
import { Card, Row, Col, Typography, Button, Space, Popconfirm } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useForm } from 'react-hook-form';
import { InputField, SelectField, RadioGroupField } from '@/components/Form';
import GoogleCustomLocationField from '@/components/Elements/GoogleCustomLocation';
import {
  PROPERTY_LENDER_OPTIONS,
  TAX_PROPERTY_USE_TYPE_OPTIONS,
  FREQUENCY_OPTIONS,
  TAX_PROPERTY_OWNER_TYPE_OPTIONS,
} from '@/constant/options';
import {
  useUpdatePersonalPropertyMutation,
  useDeletePersonalPropertyMutation,
} from '@/pages/mortgage/apis/propertyAPI';
import { displaySuccess, displayError } from '@/utils/displayMessageUtils';

const { Text } = Typography;

const YES_NO = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

type PropertyDetails = {
  property_address?: string;
  property_value?: number;
  property_use?: string;
  property_rent_frequency?: string;
  property_rent?: number;
  is_mortgage_on_property?: boolean;
  mortgage_lender?: string;
  mortgage_loan_limit?: number;
  property_owner?: string;
  mortgage_interest?: number;
};

type Property = {
  id: string | number;
  propertyDetails: PropertyDetails;
};

type EditFormValues = {
  property_address: string;
  property_value: string;
  property_use: string;
  property_rent_frequency: string;
  property_rent: number;
  is_mortgage_on_property: boolean | null;
  mortgage_lender: string;
  mortgage_loan_limit: string;
  property_owner: string | null;
  mortgage_interest: string;
};

type Props = {
  property: Property;
};

const labelFor = (
  options: { label: string; value: string | boolean | number }[],
  value: string | boolean | number | null | undefined,
): string => options.find((o) => o.value === value)?.label ?? '—';

const ViewField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div>
    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
      {label}
    </Text>
    <Text>{value || '—'}</Text>
  </div>
);

const TaxPropertyCard: React.FC<Props> = ({ property }) => {
  const d = property.propertyDetails;
  const [updateProperty, { isLoading: isSaving }] =
    useUpdatePersonalPropertyMutation();
  const [deleteProperty, { isLoading: isDeleting }] =
    useDeletePersonalPropertyMutation();

  const [isEditing, setIsEditing] = useState(false);

  const { control, handleSubmit, watch, reset } = useForm<EditFormValues>({
    defaultValues: {
      property_address: d.property_address ?? '',
      property_value: String(d.property_value ?? ''),
      property_use: d.property_use ?? '',
      property_rent_frequency: d.property_rent_frequency ?? '',
      property_rent: d.property_rent,
      is_mortgage_on_property: d.is_mortgage_on_property ?? null,
      mortgage_lender: d.mortgage_lender ?? '',
      mortgage_loan_limit: String(d.mortgage_loan_limit ?? ''),
      property_owner: d.property_owner ?? null,
      mortgage_interest: String(d.mortgage_interest ?? ''),
    },
  });

  const isMortgaged = watch('is_mortgage_on_property');

  const onCancel = () => {
    reset();
    setIsEditing(false);
  };

  const onDelete = async () => {
    try {
      await deleteProperty({ id: String(property.id) }).unwrap();
      displaySuccess('Property removed');
    } catch (err) {
      displayError(err);
    }
  };

  const onSave = handleSubmit(async (values) => {
    try {
      await updateProperty({
        data: {
          propertyDetails: {
            property_address: values.property_address,
            property_value: Number(values.property_value),
            property_use: values.property_use,
            property_rent_frequency: values.property_rent_frequency,
            property_rent: values.property_rent,
            is_mortgage_on_property: values.is_mortgage_on_property,
            mortgage_lender: values.mortgage_lender,
            mortgage_loan_limit: Number(values.mortgage_loan_limit),
            property_owner: values.property_owner,
            mortgage_interest: Number(values.mortgage_interest),
          },
        },
        property_id: String(property.id),
      }).unwrap();
      displaySuccess('Property updated');
      setIsEditing(false);
    } catch (err) {
      displayError(err);
    }
  });

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      headStyle={{ background: '#f5bf36', color: '#000' }}
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <Text strong style={{ fontSize: 15, color: '#000' }}>
            {d.property_address ?? 'Unknown address'}
          </Text>
          {!isEditing && (
            <Button size="small" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
        </div>
      }
    >
      {isEditing ? (
        <Row gutter={[16, 12]}>
          <Col xs={24}>
            <GoogleCustomLocationField
              name="property_address"
              control={control}
              placeholder="Search address..."
              location="australia"
            />
          </Col>

          <Col xs={24} md={12}>
            <SelectField
              label="Property owner"
              name="property_owner"
              control={control}
              options={TAX_PROPERTY_OWNER_TYPE_OPTIONS}
              placeholder="Select owner"
            />
          </Col>
          <Col xs={24} md={12}>
            <InputField
              label="Property value"
              name="property_value"
              control={control}
              type="number"
              placeholder="e.g. 850000"
              prefix="A$"
            />
          </Col>

          <Col xs={24} md={12}>
            <SelectField
              label="Property use"
              name="property_use"
              control={control}
              options={TAX_PROPERTY_USE_TYPE_OPTIONS}
              placeholder="Select use"
            />
          </Col>

          <Col xs={24} md={12}>
            <SelectField
              label="Rental frequency"
              name="property_rent_frequency"
              control={control}
              options={FREQUENCY_OPTIONS}
              placeholder="Select frequency"
            />
          </Col>

          <Col xs={24} md={12}>
            <InputField
              label="Rental income"
              name="property_rent"
              control={control}
              type="number"
              placeholder="e.g. 2,400"
              prefix="A$"
            />
          </Col>

          <Col xs={24} md={12}>
            <RadioGroupField
              label="Mortgage on this property?"
              name="is_mortgage_on_property"
              control={control}
              options={YES_NO}
            />
          </Col>

          {isMortgaged === true && (
            <>
              <Col xs={24} md={12}>
                <SelectField
                  label="Lender"
                  name="mortgage_lender"
                  control={control}
                  options={PROPERTY_LENDER_OPTIONS}
                  placeholder="Select lender"
                />
              </Col>
              <Col xs={24} md={12}>
                <InputField
                  label="Loan limit"
                  name="mortgage_loan_limit"
                  control={control}
                  placeholder="e.g. 420000"
                  prefix="A$"
                />
              </Col>
              <Col xs={24} md={12}>
                <InputField
                  label="Interest rate"
                  name="mortgage_interest"
                  control={control}
                  type="number"
                  placeholder="e.g. 5.5"
                  suffix="%"
                  allowDecimal
                />
              </Col>
            </>
          )}

          <Col
            xs={24}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <Popconfirm
              title="Remove this property?"
              description="This deletes the property from your profile — it will no longer appear in your Properties list."
              onConfirm={onDelete}
              okText="Remove"
              cancelText="Cancel"
              okButtonProps={{ danger: true, loading: isDeleting }}
            >
              <Button danger icon={<DeleteOutlined />} loading={isDeleting}>
                Remove property
              </Button>
            </Popconfirm>

            <Space>
              <Button onClick={onCancel}>Cancel</Button>
              <Button type="primary" onClick={onSave} loading={isSaving}>
                Save
              </Button>
            </Space>
          </Col>
        </Row>
      ) : (
        <Row gutter={[16, 12]}>
          <Col xs={24} md={12}>
            <ViewField
              label="Property owner"
              value={labelFor(
                TAX_PROPERTY_OWNER_TYPE_OPTIONS,
                d.property_owner,
              )}
            />
          </Col>
          <Col xs={24} md={12}>
            <ViewField
              label="Property value"
              value={
                d.property_value != null
                  ? `A$${d.property_value.toLocaleString()}`
                  : '—'
              }
            />
          </Col>

          <Col xs={24} md={12}>
            <ViewField
              label="Property use"
              value={labelFor(TAX_PROPERTY_USE_TYPE_OPTIONS, d.property_use)}
            />
          </Col>

          <Col xs={24} md={12}>
            <ViewField
              label="Rental frequency"
              value={labelFor(FREQUENCY_OPTIONS, d.property_rent_frequency)}
            />
          </Col>

          <Col xs={24} md={12}>
            <ViewField
              label="Rental income"
              value={
                d.property_rent != null
                  ? `A$${d.property_rent.toLocaleString()}`
                  : '—'
              }
            />
          </Col>

          <Col xs={24} md={12}>
            <ViewField
              label="Mortgage on this property?"
              value={
                d.is_mortgage_on_property == null
                  ? '—'
                  : d.is_mortgage_on_property
                    ? 'Yes'
                    : 'No'
              }
            />
          </Col>

          {d.is_mortgage_on_property && (
            <>
              <Col xs={24} md={12}>
                <ViewField
                  label="Lender"
                  value={labelFor(PROPERTY_LENDER_OPTIONS, d.mortgage_lender)}
                />
              </Col>
              <Col xs={24} md={12}>
                <ViewField
                  label="Loan limit"
                  value={
                    d.mortgage_loan_limit != null
                      ? `A$${d.mortgage_loan_limit.toLocaleString()}`
                      : '—'
                  }
                />
              </Col>
              <Col xs={24} md={12}>
                <ViewField
                  label="Interest rate"
                  value={
                    d.mortgage_interest != null
                      ? `${d.mortgage_interest}%`
                      : '—'
                  }
                />
              </Col>
            </>
          )}
        </Row>
      )}
    </Card>
  );
};

export default TaxPropertyCard;
